import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { TranslationService } from '@alfresco/adf-core';
import {
  debounceTime,
  delay,
  filter,
  pairwise,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import ClassicEditor from '@ckeditor-build/ckeditor.js';
import * as _ from 'lodash';

import { PLUGINS, DecoratorsConstant as DC, RHP_EDITOR_ID } from '../const';
import {
  ActionTypes,
  DIRECTION,
  ICitation,
  IDecorationCitation,
  IDecoratorsCounters,
  ITab,
  ITrackChange,
  NumberedCitation,
  ReferenceSource,
  TabIndex,
  UnnumberedCitation,
} from '../models';
import { DecoratorsService } from '../services/decorators.service';
import { WkDecoratorActionService } from '../services/wk-decorator-action.service';
import {
  DecorationsState,
  getCitation,
  getCitationTabIndex,
  getCopyEnable,
  getCreatedReferenceId,
  getDecoratorLoadingCompleted,
  getIsEditable,
  getNumberCitationToDeleteFromDoc,
  getNumberedContent,
  getReferenceCount,
  getRHPTrackChange,
  getSelectedNumberedCitation,
  getTrackChange,
  getUniqueUserIds,
  getUnnumberCitationContent,
  initDecoratorsLoading,
  initUserInfoFetch,
  setCitationsPrefix,
  setCitationTabIndex,
  setDeletedCitationFromRHP,
  setDeletedFromDocNumberedReferenceId,
  setDeletedNumberedReferenceId,
  setDeletedUnnumberedReferenceId,
  setEditorDependency,
  setEditorToggleTrackChange,
  setReferenceEditorClicked,
  setSelectedCitationId,
  setSelectedReferenceId,
  updateNumberedReferencesWithoutUndo,
  updateUnNumberedReferencesWithoutUndo,
} from '../state';
import * as sourceEditingHelper from './wk-decorators.source-editing.helper';
import { getObjectDiff } from '../utils';
import {
  CKEditor5,
  CKEditorComponent,
} from '../../../../app/components/ckeditor';
import { WKInsertLinkComponent } from '../../../../app/dialogs/wk-insert-link/wk-insert-link.component';
import { WkExternalPasteService } from '../../../../app/components/shared/dialogs/message-dialog';
import { WkTcCrossLinksPanelComponent } from '../../../../app/components/wk-tc-cross-links-panel/wk-tc-cross-links-panel.component';
import { SidePanelTabs } from '../../../../app/enums';
import {
  ExternalPasteConfirmationRequiredEvent,
  InsertLinkEvent,
  RouteToLinkEvent,
} from '../../../../app/model';
import {
  CitationService,
  ContextMenuEditorService,
} from '../../../../app/services';
import { ReferenceTypes } from '../../../../app/services/citations.service';
import { CommonConstants, LocalStorage } from '../../../../app/shared';
import { CkeditorConstants } from '../../../../app/components/ckeditor/constants/ckeditor.constants';

const {
  commentEvents: { DRAG_START },
  EDITOR_INSTANCE,
  TOOLBAR_ITEMS,
  CK_PLUGINS: { ANNOTATION_UIS },
} = CommonConstants;
@Component({
  selector: 'wk-decorators',
  templateUrl: 'wk-decorators.component.html',
  styleUrls: ['wk-decorators.component.scss'],
  host: {
    class: 'wk-decorator',
  },
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WKDcoratorsComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  public readonly sidebarModeConstants = CkeditorConstants.sidebarMode;
  private readonly contextmenuService: ContextMenuEditorService;
  private readonly destroy$ = new Subject<void>();
  private readonly dialogRef: MatDialog;
  private readonly externalPasteService = inject(WkExternalPasteService);
  private readonly decoratorActionsService: WkDecoratorActionService;
  private readonly citationService: CitationService;
  private editorSubscription: Subscription;
  public trackChangesAndCommentsData: ITrackChange;
  private citationId: string;
  private isChanged = false;
  private parser: DOMParser;
  private references: NumberedCitation[] = [];
  private unnumberedReferences: UnnumberedCitation[] = [];
  public referenceSources: ReferenceSource[] = [];
  public readonly citations$: Observable<IDecorationCitation> =
    this.store.select<IDecorationCitation>(getCitation);
  public readonly isCopyEnable$ = this.store.select<boolean>(getCopyEnable);
  public readonly isEditable$ = this.store.select<boolean>(getIsEditable);
  private readonly selectedNumberedCitation$: Observable<ICitation> =
    this.store.select<ICitation>(getSelectedNumberedCitation);
  public readonly uniqueUserIds$ =
    this.store.select<string[]>(getUniqueUserIds);
  public readonly isLoaded$ = this.store.select<boolean>(
    getDecoratorLoadingCompleted
  );
  public readonly entireTrackChanges$ =
    this.store.select<ITrackChange>(getTrackChange);
  public readonly notifyRefCreation$ = this.store.select<string>(
    getCreatedReferenceId
  );
  private readonly ckeditorChangeDataListener = new Subject<void>();
  public readonly rhpEditorId = RHP_EDITOR_ID;

  @ViewChild('ckContainer') public ckContainer: ElementRef;
  @ViewChild(CKEditorComponent) public ckeditor: CKEditorComponent;
  @ViewChild(WkTcCrossLinksPanelComponent)
  public tcCrossLinksPanel?: WkTcCrossLinksPanelComponent;
  @ViewChild(MatMenuTrigger) public sidebarMenuTrigger?: MatMenuTrigger;
  public editor = ClassicEditor;
  public elementRef: ElementRef;
  public isEditable: boolean;
  public selectedIndex = 0;
  public selectedNumberedReference: string;
  public tabs: ITab[];
  public toolbarConfig = {
    ...PLUGINS,
    toolbar: { ...PLUGINS.toolbar, items: [...PLUGINS.toolbar.items] },
  };
  public loadContextMenuComponent = false;
  public contextMenuObject: any;
  private ckEditorEditableElement: HTMLElement;
  public isInsertLinkDialogOpen = false;
  private isExternalPasteDialogOpen = false;
  private selectedNumberedReferenceToDeleteFromDoc: string;
  private pageInit: boolean;
  private selectedReferenceIdToMark: string;
  private lastMarkedReferenceId: string;
  private isTrackChangesEnabled: boolean;
  private sourceEditingModeListenerCleanup: (() => void) | null = null;
  private sourceEditingNavigateListenerCleanup: (() => void) | null = null;
  private sourceEditingByReferenceSavePending = false;
  private sourceEditingDialogListenerRegistered = false;
  private openCrossLinksPanelOnNextTrackChangeEvent = false;
  isLoaded: boolean;
  private readonly sidebarModeSubject = new BehaviorSubject<string>(
    this.resolveDefaultSidebarMode()
  );
  public readonly sidebarMode$ = this.sidebarModeSubject.asObservable();
  constructor(
    private readonly injector: Injector,
    private readonly store: Store<DecorationsState>,
    private readonly service: DecoratorsService,
    private readonly translationService: TranslationService,
    private readonly renderer: Renderer2
  ) {
    this.tabs = [];
    this.dialogRef = this.injector.get<MatDialog>(MatDialog);
    this.contextmenuService = this.injector.get<ContextMenuEditorService>(
      ContextMenuEditorService
    );
    this.elementRef = this.injector.get<ElementRef>(ElementRef);
    this.decoratorActionsService = this.injector.get<WkDecoratorActionService>(
      WkDecoratorActionService
    );
    this.citationService = this.injector.get<CitationService>(CitationService);
  }

  @HostListener('click', ['$event'])
  public handleIconClick(event: Event): void {
    this.decoratorActionsService.hideBalloonOnClickDisabled(
      event,
      this.ckeditor
    );
    localStorage.setItem(
      LocalStorage.RHP_CITATION_REF_CLICKED,
      JSON.stringify(true)
    );
    const target = event.target as HTMLElement;
    switch (target.title) {
      case ActionTypes.DELETE_NUMBERED:
      case ActionTypes.DELETE_UNNUMBERED:
        this.handleDelete(event);
        break;
      case ActionTypes.COPY:
        this.decoratorActionsService.handleCopy(event);
        break;
      case ActionTypes.PREVIOUS_NUMBERED:
        this.decoratorActionsService.toggleChevron(
          DIRECTION.LEFT,
          event,
          this.references
        );
        break;
      case ActionTypes.NEXT_NUMBERED:
        this.decoratorActionsService.toggleChevron(
          DIRECTION.RIGHT,
          event,
          this.references
        );
        break;
      default:
        break;
    }
    this.isEditable && this.setCitation(event);
    const contentSelected = this.decoratorActionsService.getSelectedHTMLString(
      this.ckeditor
    );
    !contentSelected &&
      this.decoratorActionsService.navigateToCitation(
        event,
        this.isEditable,
        this.ckContainer,
        this.ckeditor
      );
  }

  @HostListener('document:click', ['$event'])
  public hideOnBlur(event) {
    const target = event.target;
    if (
      target.closest('.ck-dialog-overlay') ||
      this.isExternalPasteDialogOpen
    ) {
      return;
    }
    this.decoratorActionsService.hideBalloonOnClickDisabled(
      event,
      this.ckeditor
    );
    this.toggleDocumentScroll(true);
    if (
      this.ckeditor?.editorInstance &&
      !this.ckeditor.editorInstance.editing.view.document.isFocused &&
      !this.ckContainer.nativeElement
        ?.querySelector('.editor-instance')
        ?.contains(target)
    ) {
      this.decoratorActionsService.removeValidation(this.ckContainer);
      this.decoratorActionsService.hideFindDialog(
        { nativeElement: target },
        this.ckeditor
      );
      localStorage.removeItem(LocalStorage.RHP_CITATION_REF_CLICKED);
      this.store.dispatch(setSelectedReferenceId(undefined));
      this.store.dispatch(setSelectedCitationId(undefined));
      if (
        !this.elementRef.nativeElement
          ?.querySelector(`.${EDITOR_INSTANCE} .${TOOLBAR_ITEMS}`)
          ?.contains(target)
      ) {
        this.decoratorActionsService.setPluginIsEnable(false, this.ckeditor);
      }
    }
  }
  //On document reload have to remove current scrolled Id(chevron)
  @HostListener('window:beforeunload')
  public beforeunloadHandler(): void {
    localStorage.removeItem(LocalStorage.CURRENT_LHP_SCROLLED_ID);
  }

  public ngAfterViewChecked(): void {
    const nodeList = this.ckContainer.nativeElement.querySelectorAll(
      `.${DC.NUMBERED_CITATION_CLASS}, .${DC.UNNUMBERED_CITATION_CLASS}`
    );
    if (nodeList?.length > 0) {
      this.decoratorActionsService.checkEmptyCitation(this.ckContainer);
    }
  }

  public ngOnDestroy(): void {
    const editor = this.ckeditor?.editorInstance;
    const selectedTab = this.tabs?.[this.selectedIndex]?.label?.toLowerCase();
    const { citationService } = this;
    if (editor && selectedTab) {
      try {
        this.decoratorActionsService.getSuggestionsAndCommentData(
          this.ckeditor,
          selectedTab,
          this.trackChangesAndCommentsData
        );
      } catch {
        // Editor references must still be released when persistence is unavailable.
      }
    }
    ['rhpEditorInstance', 'miniEditorInstance'].forEach((key) => {
      if (citationService[key] === editor) {
        delete citationService[key];
      }
    });
    citationService.miniEditorInstances =
      citationService.miniEditorInstances?.filter(
        (miniEditor) => miniEditor !== editor
      ) ?? [];
    [ReferenceTypes.NumberedRef, ReferenceTypes.UnnumberedRef].forEach(
      (type) => {
        if (citationService.activeMiniEditorByCitationType[type] === editor) {
          delete citationService.activeMiniEditorByCitationType[type];
        }
      }
    );

    this.sourceEditingModeListenerCleanup?.();
    this.sourceEditingModeListenerCleanup = null;
    this.sourceEditingNavigateListenerCleanup?.();
    this.sourceEditingNavigateListenerCleanup = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit(): void {
    this.parser = new DOMParser();
    this.pageInit = true;
    this.tabs = this.setPrefixAndUpdateTabs();
    this.getRefCount();

    const siteConfig = this.service.getCurrentSite();
    if (siteConfig?.sourceEditing) {
      this.toolbarConfig.toolbar.items.push('sourceEditingEnhanced');
      (this.toolbarConfig as any).sourceEditingByReference = true;
      const sourceEditingFindAndReplace = (siteConfig as any)
        ?.sourceEditingFindAndReplace;
      if (typeof sourceEditingFindAndReplace === 'boolean') {
        (this.toolbarConfig as any).sourceEditingFindAndReplace =
          sourceEditingFindAndReplace;
      }
    }

    this.store
      .select<ITrackChange>(getRHPTrackChange)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.trackChangesAndCommentsData = {
          ...data,
          suggestions: _.filter(
            data?.suggestions,
            (suggestion) => suggestion.isRHPSuggestion
          ),
          commentThreads: _.filter(
            data?.commentThreads,
            (commentThread) => commentThread.isRHPComment
          ),
        };
      });
    this.ckeditorChangeDataListener
      .pipe(debounceTime(2000), delay(150), takeUntil(this.destroy$))
      .subscribe(() => {
        this.decoratorActionsService.getSuggestionsAndCommentData(
          this.ckeditor,
          this.tabs[this.selectedIndex].label.toLowerCase()
        );
      });
    this.citationService.editorContentChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const selectedTab =
          this.tabs?.[this.selectedIndex]?.label?.toLowerCase();
        if (!this.ckeditor?.editorInstance || !selectedTab) {
          return;
        }

        this.isChanged = true;
        this.updateChanges();
        this.decoratorActionsService.getSuggestionsAndCommentData(
          this.ckeditor,
          selectedTab,
          this.trackChangesAndCommentsData
        );
      });
    this.isLoaded$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((isLoaded) => {
        this.isLoaded = isLoaded;
        !isLoaded && this.store.dispatch(initDecoratorsLoading());
        isLoaded && this.store.dispatch(setEditorDependency());
      });
    this.getCitations();
    this.citationService.trackChangesClickedInMiniEditor
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          this.openCrossLinksPanelOnNextTrackChangeEvent &&
          this.canDisplayCrossLinksPanel()
        ) {
          this.openCrossLinksPanelOnNextTrackChangeEvent = false;
          this.setSelectedMode('wideSidebar');
          this.hideInlineBalloonsForWideSidebar();
          this.tcCrossLinksPanel?.show();
          return;
        }

        this.openCrossLinksPanelOnNextTrackChangeEvent = false;

        if (
          this.tcCrossLinksPanel?.isVisible &&
          this.canDisplayCrossLinksPanel()
        ) {
          // Keep visible panel content updated, but do not auto-open on generic editor changes.
          this.tcCrossLinksPanel.show();
          return;
        }

        this.toggleCrossLinksPanelByMode();
      });
    this.citationService.changePanel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.toggleCrossLinksPanelByMode();
      });
    this.uniqueUserIds$
      .pipe(pairwise(), takeUntil(this.destroy$))
      .subscribe(([previousIds, ids]) => {
        ids.length &&
          !_.isEqual(previousIds, ids) &&
          this.store.dispatch(initUserInfoFetch({ ids }));
      });
    this.store
      .select(getCitationTabIndex)
      .pipe(takeUntil(this.destroy$))
      .subscribe((index: number) => {
        this.setSelectedTab(index);
      });
    this.selectedNumberedCitation$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (citation) =>
          citation?.citationId !== this.selectedNumberedReference &&
          this.handleNumberedReferenceSelection(citation?.citationId)
      );
    this.isEditable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        this.isEditable = value;
        // The review overlay must close immediately when the document stops being editable.
        this.toggleCrossLinksPanelByMode();
        // The tc-panel-btn menu must not remain reachable once the button itself is hidden.
        !value && this.sidebarMenuTrigger?.closeMenu();
      });
    this.decoratorActionsService.initContextMenuSubscriptions(
      this as any,
      this.toggleDocumentScroll.bind(this)
    );
    this.subscribeToNumberedRefDelete();
    this.notifyRefCreation$
      .pipe(takeUntil(this.destroy$))
      .subscribe((newNum) => {
        this.selectedReferenceIdToMark = this.isTrackChangesEnabled && newNum;
      });

    // Attach click listener for track changes detection in RHP
    setTimeout(() => {
      this.attachTrackChangesClickListener();
    }, 500);
  }

  public onChange(): void {
    this.isChanged = true;
    setTimeout(() => {
      this.validateCitation();
    }, 0);
    this.loadContextMenuComponent = false;
  }

  public onMouseLeave(): void {
    !this.isExternalPasteDialogOpen && this.isChanged && this.updateChanges();
  }

  private updateChanges(): void {
    if (this.sourceEditingByReferenceSavePending) {
      this.sourceEditingByReferenceSavePending = false;
      this.isChanged = false;
      return;
    }

    if (this.ckeditor?.editorInstance) {
      const selectedTab = this.tabs[this.selectedIndex].label.toLowerCase();
      const rhpEditorData = this.parser.parseFromString(
        this.ckeditor.editorInstance.getData(),
        'text/html'
      );
      const numBlock: NodeList = rhpEditorData.querySelectorAll(
        `.${DC.NUMBERED_CITATION_CLASS}`
      );
      const unnumBlock: NodeList = rhpEditorData.querySelectorAll(
        `.${DC.UNNUMBERED_CITATION_CLASS}`
      );
      (numBlock.length > 0 || this.selectedIndex === 0) &&
        this.decoratorActionsService.onEditCitations({
          nodeList: numBlock,
          references: _.cloneDeep(this.references),
          action: updateNumberedReferencesWithoutUndo,
          ckeditor: this.ckeditor,
          selectedTab,
          trackChangesAndCommentsData: this.trackChangesAndCommentsData,
        });
      (unnumBlock.length > 0 || this.selectedIndex === 1) &&
        this.decoratorActionsService.onEditCitations({
          nodeList: unnumBlock,
          references: this.unnumberedReferences,
          action: updateUnNumberedReferencesWithoutUndo,
          ckeditor: this.ckeditor,
          selectedTab,
          trackChangesAndCommentsData: this.trackChangesAndCommentsData,
        });
      if (numBlock.length > 0 || unnumBlock.length > 0) {
        this.decoratorActionsService.checkEmptyCitation(this.ckContainer);
      }
      this.isChanged = false;
      this.decoratorActionsService.setPluginIsEnable(false, this.ckeditor);
    }
  }

  public onReady(): void {
    const { heading, selector } = this.tabs[this.selectedIndex];
    this.ckEditorEditableElement = this.elementRef.nativeElement.querySelector(
      `.${CommonConstants.editorEditableInline}`
    );
    // BUGFIX-001: Store reference to RHP editor so track changes panel can use it instead of main editor
    if (this.ckeditor?.editorInstance) {
      this.citationService.rhpEditorInstance = this.ckeditor.editorInstance;
    }
    if (this.editorSubscription) {
      this.editorSubscription.unsubscribe();
    }
    this.editorSubscription = this.store
      .select(selector({ heading }))
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(this.bindRHPEditor.bind(this));

    // Add null check before attaching event listeners to avoid errors in unit tests
    // where the ckEditorEditablePlaceholder element doesn't exist in the DOM
    const ckEditorPlaceholder = this.elementRef.nativeElement.querySelector(
      CommonConstants.ckEditorEditablePlaceholder
    );

    if (ckEditorPlaceholder) {
      this.renderer.listen(ckEditorPlaceholder, 'mousedown', (event) => {
        this.decoratorActionsService.onMouseEventInCK5Editor(event, {
          ckeditor: this.ckeditor,
          ckEditorEditableElement: this.ckEditorEditableElement,
          elementRef: this.elementRef,
        });
      });
      this.renderer.listen(
        ckEditorPlaceholder,
        CommonConstants.CONTEXT_MENU,
        this.openContextMenuClick.bind(this)
      );
    }

    this.ckeditor?.editorInstance?.plugins
      ?.get?.(CommonConstants.CK_PLUGINS.CONTEXTUAL_BALLOON)
      ?.view?.hide?.();
    this.ckeditor?.editorInstance?.editing.view.document.on(
      DRAG_START,
      (evt, data) => {
        evt.stop();
        data.preventDefault();
      },
      { priority: 'highest' }
    );
    this.ckeditor?.editorInstance?.commands
      .get(CommonConstants.CK_PLUGINS.CMD_TRACK_CHANGES)
      .on('change:value', (evt) => {
        this.isTrackChangesEnabled = evt.source.value;
        this.store.dispatch(
          setEditorToggleTrackChange({
            rhpTrackChangesEnabled: this.isTrackChangesEnabled,
          })
        );
      });
    this.ckeditor?.editorInstance?.commands?.get?.('trackChanges')?.on(
      'change:value',
      (_evt, _propName, isOn: boolean) => {
        localStorage.setItem(
          LocalStorage.RHP_TRACK_CHANGES_ENABLE_DISABLE_LISTENER,
          JSON.stringify(isOn)
        );
      },
      { priority: 'highest' }
    );
    this.decoratorActionsService.getTrackChangesConfigData(
      this.ckeditor,
      WKDcoratorsComponent.readTrackChangesListenerPreference()
    );
    this.syncSourceEditingLabels();
    this.bindSourceEditingDialogTitle();
    sourceEditingHelper.bindSourceEditingModeListener(this);
    sourceEditingHelper.bindSourceEditingNavigateListener(this);
    sourceEditingHelper.bindSourceEditingOpenListener(this);
    sourceEditingHelper.bindSourceEditingReplaceListener(this);
    !this.isEditable &&
      this.ckeditor?.editorInstance?.plugins
        ?.get?.(ANNOTATION_UIS)
        ?.deactivateAll?.();
    this.activeEventListener();

    // PHASE 15: Subscribe to CKEditor 5 track changes commands
    // Listen for accept/discard suggestions commands to trigger panel refresh
    this.subscribeToTrackChangesCommands();
  }

  /**
   * change:data Listens to change events when user makes changes in the editor.
   * change:activeMarkers Listens to changes in the active markers of the Track Changes plugin if user only does undo/redo from main editor.
   */
  private activeEventListener(): void {
    try {
      this.ckeditor?.editorInstance?.model?.document?.on(
        'change:data',
        () => this.ckeditorChangeDataListener.next(),
        { priority: 'high' }
      );
      this.ckeditor?.editorInstance?.plugins
        ?.get('TrackChangesEditing')
        ?.on?.(
          'change:activeMarkers',
          () => this.ckeditorChangeDataListener.next(),
          { priority: 'high' }
        );
    } catch (err) {
      throw new Error(`Error while activating event listeners: ${err.message}`);
    }
  }

  /**
   * PHASE 15: Subscribe to CKEditor 5 track changes suggestion commands.
   * When the user executes any of the track changes commands (accept/discard all/selected suggestions),
   * this triggers a refresh of the cross-links panel suggestions.
   *
   * Note: Mini editors are null initially and get instantiated when tabs are selected or RHP is opened.
   * We subscribe to miniEditorRegisteredForType$ to detect when actual editor instances are available.
   */
  private subscribeToTrackChangesCommands(): void {
    try {
      // Subscribe to when mini editors are registered/updated with real instances
      this.citationService.miniEditorRegisteredForType$
        .pipe(takeUntil(this.destroy$))
        .subscribe((citationType: string) => {
          // Get the mini editor instance - it may not be null this time
          const miniEditor =
            this.citationService.activeMiniEditorByCitationType?.[citationType];

          if (miniEditor) {
            this.attachTrackChangesCommandListeners(miniEditor);
          }
        });
    } catch (error) {
      // Silently continue - mini editors may not be available in all contexts
    }
  }

  /**
   * Attach listeners to track changes commands on a specific mini editor instance
   * Uses model document change events since track changes commands modify the document
   */
  private attachTrackChangesCommandListeners(editor: CKEditor5.Editor): void {
    try {
      // Listen to model document changes which occur when track changes commands execute
      // Track changes commands (acceptAllSuggestions, discardAllSuggestions, etc.) all modify the model
      if (editor?.model?.document) {
        editor.model.document.on('change:data', () => {
          // Wait for CKEditor to process the change before refreshing
          setTimeout(() => {
            this.citationService.trackChangesClickedInMiniEditor.next();
          }, 100);
        });
      }
    } catch (error) {
      // Silently continue - editor may not be ready
    }
  }

  /**
   * This method used to bind editor with the value
   * @param value {String} - The value to be set in the editor
   */
  private bindRHPEditor(value: string): void {
    if (
      this.parser
        .parseFromString(value, 'text/html')
        .querySelector('.tab-heading')?.firstChild?.textContent ===
      this.tabs[this.selectedIndex]?.heading
    ) {
      const oldValue = this.parser.parseFromString(
        this.ckeditor.editorInstance.getData(),
        'text/html'
      ).body.innerText;

      this.ckeditor?.editorInstance.setData(value);
      const newValue = this.parser.parseFromString(
        this.ckeditor.editorInstance.getData(),
        'text/html'
      ).body.innerText;
      this.isChanged = false;
      /** After the content is loaded */
      setTimeout(() => {
        /** Select numbered reference */
        if (this.selectedNumberedReference) {
          this.selectNumberedReference(this.selectedNumberedReference);
          this.selectedNumberedReference = null;
        }
        /** Mark new reference as insertion for track change */
        if (
          this.selectedReferenceIdToMark &&
          this.selectedReferenceIdToMark !== this.lastMarkedReferenceId
        ) {
          this.decoratorActionsService.markNewReferenceAsInsertion(
            this.ckeditor.editorInstance,
            this.selectedReferenceIdToMark
          );
          this.lastMarkedReferenceId = this.selectedReferenceIdToMark;
        }
      }, 0);
      this.decoratorActionsService.checkEmptyCitation(this.ckContainer);
      setTimeout(() => {
        newValue !== oldValue && !this.pageInit && this.updateChanges();
        this.pageInit = false;
      }, 500);
    }
  }

  public openContextMenu(event): void {
    this.loadContextMenuComponent = true;
    event.contextMenuObj.splice(0, 1);
    this.contextMenuObject = this.contextmenuService.removeElementsByName(
      event.contextMenuObj,
      [CommonConstants.rightClickOptions.LINK_TO]
    );
  }

  public saveDocument(): void {
    if (!this.isInsertLinkDialogOpen && !this.isExternalPasteDialogOpen) {
      if (this.sourceEditingByReferenceSavePending) {
        this.sourceEditingByReferenceSavePending = false;
        this.isChanged = false;
        this.decoratorActionsService.setPluginIsEnable(false, this.ckeditor);
        return;
      }

      this.isChanged = true;
      this.onMouseLeave();
      this.decoratorActionsService.setPluginIsEnable(false, this.ckeditor);
    }
  }

  public selectedTabChange(evt: MatTabChangeEvent): void {
    if (this.selectedIndex !== evt.index) {
      this.selectedIndex = evt.index;

      // Update citationEditType to match the active RHP tab (BUGFIX-001)
      // Tab index 0 (Numbered) → REFERENCES, Tab index 1 (Unnumbered) → UNNUMBERED_REFERENCES
      // Tab indices 2+ are deferred to future phases
      if (evt.index === 0) {
        this.citationService.citationEditType = ReferenceTypes.NumberedRef;
      } else if (evt.index === 1) {
        this.citationService.citationEditType = ReferenceTypes.UnnumberedRef;
      }

      // Sync the main editor's citationTabIndex state so that Accept All/Reject All buttons
      // in the main CKEditor toolbar scope to the correct Numbered/Unnumbered subset (FR-004/FR-005/FR-006).
      // Without this, the main editor's activeCitationTabIndex stays stale, filtering out all
      // suggestions when the user manually switches RHS tabs then clicks Accept/Reject All.
      this.store.dispatch(setCitationTabIndex({ index: evt.index }));

      sourceEditingHelper.refreshSourceEditingContextForTab(this, evt.index);

      // BUGFIX-001: Defer rhpTabChanged emission to let onReady() complete its store binding.
      // onReady() uses debounceTime(500), so we delay 100ms to ensure it starts subscription,
      // then the panel waits for miniEditorRegisteredForType$ signal with its own timeout.
      // Only emit when index actually changes (not when user selects already-selected tab)
      setTimeout(() => {
        this.citationService.rhpTabChanged.next();
      }, 100);
    }
    this.toggleCrossLinksPanelByMode();
    this.onReady();
    this.syncSourceEditingLabels();
  }

  /**
   * Attach click listener to the entire RHP to detect track changes clicks
   */
  private attachTrackChangesClickListener(): void {
    // Use the component's own native element as the RHP container
    const rhpElement = this.elementRef.nativeElement;

    if (!rhpElement) {
      return;
    }

    rhpElement.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;

      if (!target) {
        return;
      }

      // Walk up the DOM to find a track changes element
      let element: HTMLElement | null = target;
      for (let i = 0; i < 10 && element; i++) {
        const hasTrackChangeAttr =
          element.hasAttribute('data-suggestion') ||
          element.hasAttribute('data-insertion') ||
          element.hasAttribute('data-deletion') ||
          element.hasAttribute('data-suggestion-start-before') ||
          element.hasAttribute('data-suggestion-start-after') ||
          element.hasAttribute('data-suggestion-end-before') ||
          element.hasAttribute('data-suggestion-end-after');

        if (hasTrackChangeAttr) {
          const clickedSuggestionId =
            this.extractSuggestionIdFromElement(element);
          if (!clickedSuggestionId) {
            element = element.parentElement;
            continue;
          }

          if (this.ckeditor?.editorInstance) {
            this.citationService.miniEditorInstance =
              this.ckeditor.editorInstance;

            // Determine citation type from currently selected tab
            // Tab index 0 = Numbered References (REFERENCES), Tab index 1 = Unnumbered References (UNNUMBERED_REFERENCES)
            let activeCitationType: string | undefined;
            if (this.selectedIndex === 0) {
              activeCitationType = ReferenceTypes.NumberedRef;
            } else if (this.selectedIndex === 1) {
              activeCitationType = ReferenceTypes.UnnumberedRef;
            }

            if (
              activeCitationType &&
              this.citationService.activeMiniEditorByCitationType &&
              activeCitationType in
                this.citationService.activeMiniEditorByCitationType
            ) {
              this.citationService.activeMiniEditorByCitationType[
                activeCitationType
              ] = this.ckeditor.editorInstance;
              // Emit to notify subscribers that a mini editor has been registered
              this.citationService.miniEditorRegisteredForType$.next(
                activeCitationType
              );
            }
          }

          this.openCrossLinksPanelOnNextTrackChangeEvent = true;
          this.citationService.selectedTrackChangeId.next(clickedSuggestionId);
          this.citationService.trackChangesClickedInMiniEditor.next();
          break;
        }

        element = element.parentElement;
      }
    });
  }

  private extractSuggestionIdFromElement(element: HTMLElement): string | null {
    if (!element) {
      return null;
    }

    const directAttributes = [
      'data-suggestion',
      'data-insertion',
      'data-deletion',
      'data-suggestion-start-before',
      'data-suggestion-start-after',
      'data-suggestion-end-before',
      'data-suggestion-end-after',
    ];

    for (const attributeName of directAttributes) {
      const value = element.getAttribute(attributeName);
      const normalized = this.normalizeSuggestionId(value);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeSuggestionId(
    rawValue: string | null | undefined
  ): string | null {
    if (!rawValue) {
      return null;
    }

    const uuidMatch = rawValue.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
    );
    if (uuidMatch?.[0]) {
      return uuidMatch[0];
    }

    const segments = rawValue
      .split(':')
      .map((segment) => segment.trim())
      .filter((segment) => !!segment);
    if (segments.length) {
      return segments[segments.length - 1];
    }

    const normalized = rawValue.trim();
    return normalized || null;
  }

  public setSidebarMode(mode: string): void {
    // Update the mode state (this triggers ngClass binding which applies CSS)
    this.sidebarModeSubject.next(mode);

    // Apply annotation UI mode once when the user changes the tc-panel-btn mode.
    this.setSelectedMode(
      mode === CkeditorConstants.sidebarMode.WIDE_SIDEBAR
        ? CkeditorConstants.sidebarModeConfigValue.WIDE_SIDEBAR
        : CkeditorConstants.sidebarModeConfigValue.INLINE
    );
    if (mode === CkeditorConstants.sidebarMode.INLINE) {
      this.restoreInlineBalloonsForInlineMode();
    }

    // Emit for any mini editors that subscribe to this
    this.citationService.sidebarModeChanged.next(mode);

    if (
      mode === CkeditorConstants.sidebarMode.WIDE_SIDEBAR &&
      this.canDisplayCrossLinksPanel()
    ) {
      this.tcCrossLinksPanel?.show();
    }

    // Immediately sync floating panel visibility with selected mode.
    this.toggleCrossLinksPanelByMode();

    // Close the dropdown menu after selection
    this.sidebarMenuTrigger?.closeMenu();
  }

  public onSidebarModeClick(event: Event): void {
    // Stop event propagation to prevent triggering track changes click detection
    event.stopPropagation();
  }

  public toggleCrossLinksPanelByMode(): void {
    if (!this.tcCrossLinksPanel) {
      return;
    }

    if (!this.canDisplayCrossLinksPanel()) {
      this.tcCrossLinksPanel.hide();
    }
  }

  private canDisplayCrossLinksPanel(): boolean {
    const currentMode = this.sidebarModeSubject.value;
    const isCrossLinksTabActive = WKDcoratorsComponent.isCrossLinksViewActive(
      this.elementRef?.nativeElement
    );
    return (
      this.isEditable &&
      isCrossLinksTabActive &&
      currentMode === CkeditorConstants.sidebarMode.WIDE_SIDEBAR
    );
  }

  // Falls back to Wide sidebar whenever the current site cannot be resolved, or its configured value is absent, malformed, or unrecognized.
  private resolveDefaultSidebarMode(): string {
    let configuredMode: string;
    try {
      configuredMode =
        this.service.getCurrentSite()?.trackChanges?.defaultSidebarMode;
    } catch {
      // getCurrentSite() can throw if site config isn't loaded yet at this early, pre-ngOnInit field-initializer timing.
      configuredMode = undefined;
    }
    return configuredMode === CkeditorConstants.sidebarModeConfigValue.INLINE
      ? CkeditorConstants.sidebarMode.INLINE
      : CkeditorConstants.sidebarMode.WIDE_SIDEBAR;
  }

  private static isCrossLinksViewActive(hostElement: HTMLElement): boolean {
    if (!hostElement) {
      return false;
    }
    const tabBody = hostElement.closest('.mat-tab-body');
    if (tabBody) {
      return tabBody.classList.contains('mat-tab-body-active');
    }
    const styles = window.getComputedStyle(hostElement);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  // Defends against a stale/corrupted stored value (e.g. the literal string "undefined") throwing during JSON.parse.
  private static readTrackChangesListenerPreference(): boolean {
    const storedValue = localStorage.getItem(
      LocalStorage.RHP_TRACK_CHANGES_ENABLE_DISABLE_LISTENER
    );
    try {
      return storedValue == null ? true : JSON.parse(storedValue);
    } catch {
      return true;
    }
  }

  private applyToAllEditors(
    operation: (editor: CKEditor5.Editor) => void
  ): void {
    const editors = [
      this.ckeditor?.editorInstance,
      ...(this.citationService.miniEditorInstances || []),
      this.citationService.miniEditorInstance,
    ].filter((editor): editor is CKEditor5.Editor => !!editor);

    const uniqueEditors = editors.filter(
      (editor, index, arr) => arr.indexOf(editor) === index
    );
    uniqueEditors.forEach(operation);
  }

  private hideInlineBalloonsForWideSidebar(): void {
    this.applyToAllEditors((editor) => {
      try {
        const annotationUis = editor.plugins?.get(ANNOTATION_UIS);
        annotationUis?.deactivate?.('inline');
      } catch (_error) {
        // Ignore if editor does not expose inline annotation UI.
      }

      try {
        const contextualBalloon = editor.plugins?.get(
          CommonConstants.CK_PLUGINS.CONTEXTUAL_BALLOON
        );
        contextualBalloon?.view?.hide?.();
      } catch (_error) {
        // Ignore if editor has no contextual balloon instance.
      }
    });
  }

  private restoreInlineBalloonsForInlineMode(): void {
    this.applyToAllEditors((editor) => {
      try {
        const annotationUis = editor.plugins?.get(ANNOTATION_UIS);
        annotationUis?.activate?.('inline');
      } catch (_error) {
        // Ignore if inline mode is not available for this editor.
      }

      try {
        const annotations = editor.plugins?.get?.(
          CommonConstants.CK_PLUGINS.ANNOTATIONS
        );
        annotations?.refreshVisibility?.();
        annotations?.refreshPositioning?.();
      } catch (_error) {
        // Ignore if annotations plugin is not available.
      }
    });
  }

  public setSelectedMode(mode: string): void {
    this.applyToAllEditors((miniEditor) => {
      try {
        const annotationUis = miniEditor.plugins?.get(ANNOTATION_UIS);
        if (annotationUis) {
          annotationUis.deactivateAll();
          annotationUis.activate(mode);
        }
      } catch (_error) {
        // Silently ignore errors
      }
    });
  }

  public tabTrackByFn(index: number, item: ITab): string {
    return index + item.label;
  }

  public onExternalPasteConfirmationRequired(
    event: ExternalPasteConfirmationRequiredEvent
  ): void {
    const plainText = event?.plainText;
    if (!plainText?.trim()) {
      return;
    }
    if (!this.externalPasteService.shouldSkipWarning()) {
      this.isExternalPasteDialogOpen = true;
      this.tcCrossLinksPanel?.hide();
    }
    this.externalPasteService
      .confirmPaste$(plainText)
      .pipe(
        tap(() => {
          this.isExternalPasteDialogOpen = false;
        }),
        filter(Boolean)
      )
      .subscribe(() => {
        this.ckeditor?.editorInstance?.focus?.();
        this.externalPasteService.insertPlainText(
          this.ckeditor?.editorInstance,
          plainText
        );
      });
  }

  public onInsertLink(insertLinkEvent: InsertLinkEvent): void {
    this.isChanged = false;
    this.isInsertLinkDialogOpen = true;
    this.store.dispatch(
      setReferenceEditorClicked({
        referenceEditor: {
          isReferenceEditorClicked: true,
          referenceEditorInstance: this.ckeditor,
        },
      })
    );
    const dialog = this.dialogRef.open(WKInsertLinkComponent, {
      maxHeight: '400vh',
      panelClass: 'wk-insert-media-dialog',
      width: '80%',
    });
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isInsertLinkDialogOpen = false;
        this.isChanged = true;
        this.removeFocusFromEditor();
      });
    dialog.componentInstance?.setLinkEvent(insertLinkEvent);
  }

  public routeToLink(event: RouteToLinkEvent): void {
    (this as any)._setClickDetails = event;
    this.decoratorActionsService.setEditorClicked.next(
      CommonConstants.editor.RIGHT_HAND_EDITOR
    );
  }

  public editLink(data): void {
    let linkAddress = data.actionDetails.linkAddress;
    if (linkAddress.indexOf('mailto:') === 0) {
      linkAddress = linkAddress.replace('mailto:', '');
    }

    const linkElement = this.elementRef.nativeElement?.querySelector(
      `[id='${data.actionDetails.idUnique}']`
    );
    const insertLinkData: InsertLinkEvent = {
      editor: this.ckeditor.editorInstance,
      arguments: {
        source: data.actionDetails,
      } as CKEditor5.EventInfo<'insertlink'>,
      linkAttributes: {
        linkText: data.actionDetails.linkText,
        linkAddress,
        linkElement,
      },
    };
    this.onInsertLink(insertLinkData);
    localStorage.setItem(LocalStorage.EDIT_LINK, data.actionDetails.id);
  }

  // #endregion Public Methods (12)

  // #region Private Methods (26)
  private deleteNumberedReference(citationId: string): void {
    const numberedRef = _.find(this.references, { citationId });
    numberedRef?.referencedBy?.length > 0 &&
      this.decoratorActionsService
        .deleteNumberedReference(numberedRef)
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            this.store.dispatch(
              setDeletedCitationFromRHP({ citationId: numberedRef.citationId })
            );
          }
        });
  }

  private deleteUnnumberedReference(citationId: string): void {
    this.decoratorActionsService
      .deleteUnNumberedReference()
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          const ref = _.find(this.unnumberedReferences, { citationId });
          this.decoratorActionsService.removeSuggestionsFromReference(
            ref,
            this.trackChangesAndCommentsData
          );
          this.store.dispatch(setDeletedUnnumberedReferenceId({ citationId }));
        }
      });
  }

  private getCitations(): void {
    this.citations$.pipe(takeUntil(this.destroy$)).subscribe((citation) => {
      const { unnumbered_references, references } = citation;
      this.unnumberedReferences = unnumbered_references;
      this.references = references;
      this.referenceSources = [
        {
          list: () => this.references,
          setList: (refs) => {
            this.references = refs;
          },
          dispatchUpdate: (refs) =>
            this.store.dispatch(
              updateNumberedReferencesWithoutUndo({ references: refs })
            ),
          onSelect: (citationId) => {
            this.store.dispatch(setSelectedReferenceId({ id: citationId }));
            this.scrollToCitation(citationId);
          },
        },
        {
          list: () => this.unnumberedReferences,
          setList: (refs) => {
            this.unnumberedReferences = refs;
          },
          dispatchUpdate: (refs) =>
            this.store.dispatch(
              updateUnNumberedReferencesWithoutUndo({ references: refs })
            ),
          onSelect: (citationId) => {
            this.scrollToCitation(citationId);
          },
        },
      ];
    });
  }

  /**
   * This method is used to set the TAB based on the count of references, unnumbered references & keywords
   * This method is called when the count of references, unnumbered references & keywords changes
   * @param previousCount - Previous count of number, unnumbered & keyword references
   * @param counts - Current count of number, unnumbered & keyword references
   */
  private setDisplayTab([previousCount, counts]: IDecoratorsCounters[]): void {
    const { referencesCount, unnumberedReferencesCount, keywordCount } = counts;
    const diff = getObjectDiff(previousCount, counts);
    _.forEach(
      [referencesCount, unnumberedReferencesCount, keywordCount],
      (count, i) => {
        this.tabs[i].count = count;
        this.tabs[i].disabled = count <= 0;
      }
    );
    if (diff.length > 0) {
      if (this.tabs[TabIndex[diff[0]]].disabled) {
        const firstEnabledTab = _.findIndex(this.tabs, { disabled: false });
        if (firstEnabledTab >= 0) {
          this.setSelectedTab(firstEnabledTab);
        } else {
          this.citationService.changeSidePanelTabTo(SidePanelTabs.Content);
        }
      } else {
        this.setSelectedTab(TabIndex[diff[0]]);
      }
    }
  }

  private getRefCount(): void {
    this.store
      .select(getReferenceCount)
      .pipe(takeUntil(this.destroy$), pairwise())
      .subscribe(this.setDisplayTab.bind(this));
  }

  private handleDelete(event): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const parent = target.closest(`[${DC.DATA_CITATION_ID}]`);
    const citationId = parent.getAttribute(DC.DATA_CITATION_ID);
    if (parent.classList.contains(DC.UNNUMBERED_CITATION_CLASS)) {
      this.deleteUnnumberedReference(citationId);
    } else if (parent.classList.contains(DC.NUMBERED_CITATION_CLASS)) {
      this.deleteNumberedReference(citationId);
    } else {
      return;
    }
  }

  private handleNumberedReferenceSelection(citationId: string): void {
    if (this.selectedIndex !== TabIndex.referencesCount) {
      this.selectedNumberedReference = citationId;
      this.setSelectedTab(TabIndex.referencesCount);
    } else {
      setTimeout(() => {
        this.selectNumberedReference(citationId);
      }, 0);
    }
  }

  private selectNumberedReference(citationId: string): boolean {
    return this.scrollToCitation(citationId);
  }

  private scrollToCitation(citationId: string): boolean {
    const element = this.elementRef.nativeElement.querySelector(
      `[${DC.DATA_CITATION_ID}="${citationId}"]`
    );
    element && element.scrollIntoView();
    return !!element;
  }

  private setCitation(event): void {
    this.decoratorActionsService.hideBalloonOnClickDisabled(
      event,
      this.ckeditor
    );
    const target = event.target as HTMLElement;
    try {
      /**
       * This if part is executed when clicked inside the editable part in RHP
       * else part covers when clicked outside the editable part in editor.
       */
      if (
        !target.classList?.contains(DC.BG_CLASS) &&
        target.closest(`.${DC.CITATION_TEXT}`)
      ) {
        const parent = target.closest('[data-citation-id]');
        this.citationId = parent.getAttribute(DC.DATA_CITATION_ID);
        /**
         * This is to avoid select All content in RHP.
         * It will select content of respective editable element
         */
        const rhpEditorInstance = this.ckeditor.editorInstance;
        const {
          model: {
            document: { selection },
          },
        } = rhpEditorInstance;
        const modelElement = _.find(
          _.reverse(selection.anchor.getAncestors()),
          (ele) => ele.name !== CommonConstants.spanTagName
        );
        rhpEditorInstance.commands
          .get(CommonConstants.CK_PLUGINS.SELECT_ALL)
          .on('execute', this.selectAllInRHP.bind(this, modelElement));
        this.decoratorActionsService.applyCitationSelectionClasses(
          this.ckContainer,
          this.citationId,
          target.textContent,
          this.renderer
        );
      } else {
        if (
          !this.elementRef.nativeElement
            ?.querySelector(`.${EDITOR_INSTANCE} .${TOOLBAR_ITEMS}`)
            ?.contains(target)
        ) {
          this.disablePlugins(
            [CommonConstants.LINK_PLUGIN_CMD, CommonConstants.INSERT_TEXT_CMD],
            target
          );
          this.removeFocusFromEditor();
        }
      }
    } catch (err) {
      console.warn(err);
    }
  }

  private selectAllInRHP(modelElement) {
    this.ckeditor.editorInstance.model.change((writer) => {
      const startPos = writer.createPositionAt(modelElement, 0);
      const endPos = writer.createPositionAt(modelElement, 'end');
      const range = writer.createRange(startPos, endPos);
      writer.setSelection(range);
    });
  }

  private removeFocusFromEditor() {
    if (!this.ckeditor?.editorInstance?.editing?.view) {
      return;
    }
    this.ckeditor.editorInstance.editing.view.domRoots.get('main')?.blur();
    this.ckeditor.editorInstance.editing.view.document.isFocused = false;
  }

  private disablePlugins(pluginList, target) {
    pluginList.forEach((plugin) => {
      if (
        (!this.elementRef.nativeElement
          ?.querySelector('.editor-instance .ck-dropdown')
          ?.contains(target) &&
          plugin === CommonConstants.INSERT_TEXT_CMD) ||
        (!this.elementRef.nativeElement
          ?.querySelector('.editor-instance .insertLinkIcon')
          ?.contains(target) &&
          plugin === CommonConstants.LINK_PLUGIN_CMD)
      ) {
        this.ckeditor.editorInstance.commands.get(plugin).isEnabled = false;
      }
    });
  }

  private setPrefixAndUpdateTabs(): ITab[] {
    const {
      referenceTab: {
        subTabs: { numbered, unnumbered, keyword, more },
      },
    } = this.service.getCurrentSite();

    this.store.dispatch(
      setCitationsPrefix({
        referencePrefix: numbered.counter,
        prefix: numbered.prefix,
        sufix: numbered.sufix,
        counterCase: numbered.counterCase,
        unnumberReferencePrefix: unnumbered.counter,
      })
    );

    return [
      {
        label: this.translationService.instant(numbered.label),
        selector: getNumberedContent,
        count: 0,
        heading: this.translationService.instant(numbered.heading),
      },
      {
        label: this.translationService.instant(unnumbered.label),
        selector: getUnnumberCitationContent,
        count: 0,
        heading: this.translationService.instant(unnumbered.heading),
      },
      {
        label: this.translationService.instant(keyword.label),
        disabled: keyword.enabled,
        count: 0,
        heading: this.translationService.instant(keyword.heading),
      },
      {
        label: this.translationService.instant(more.label),
        disabled: more.enabled,
        count: 0,
        heading: this.translationService.instant(more.heading),
      },
    ];
  }

  private setSelectedTab(index: number): void {
    this.selectedIndex = index || 0;
  }

  private getActiveSourceLabel(): string {
    const activeTabLabel = this.tabs?.[this.selectedIndex]?.label;

    return activeTabLabel
      ? `${activeTabLabel} ${CommonConstants.CK_PLUGINS.EDIT_SOURCE_LABEL}`
      : CommonConstants.CK_PLUGINS.EDIT_SOURCE_LABEL;
  }

  private syncSourceEditingLabels(): void {
    this.decoratorActionsService.updateSourceEditingLabel(
      this.ckeditor,
      this.getActiveSourceLabel()
    );
  }

  private bindSourceEditingDialogTitle(): void {
    if (this.sourceEditingDialogListenerRegistered) {
      return;
    }
    this.decoratorActionsService.bindSourceEditingDialogTitle(
      this.ckeditor,
      () => this.getActiveSourceLabel()
    );
    this.sourceEditingDialogListenerRegistered = true;
  }

  private validateCitation(): void {
    if (!this.ckContainer) {
      return;
    }
    this.decoratorActionsService.validateCitationBlock(
      this.ckContainer,
      this.citationId,
      this.renderer
    );
  }

  private openContextMenuClick(event): void {
    if (event.target.closest(`.${DC.CITATION_TEXT}`)) {
      event.target.click();
      this.toggleDocumentScroll(false);
      this.ckeditor?.editorInstance?.execute(CommonConstants.CONTEXT_MENU, {
        value: event,
      });
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // this method is used by tenant. That's why protected is here
  protected toggleDocumentScroll(enableToggle: boolean) {
    const editorElement = this.ckContainer?.nativeElement?.querySelector(
      CommonConstants.ckEditorMainClassName
    );
    editorElement &&
      this.renderer?.setStyle(
        editorElement,
        'overflow-y',
        enableToggle ? 'auto' : 'hidden'
      );
  }

  private subscribeToNumberedRefDelete(): void {
    this.store
      .select(getNumberCitationToDeleteFromDoc)
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (data) =>
            data?.citationId !== this.selectedNumberedReferenceToDeleteFromDoc
        )
      )
      .subscribe(this.handleNumberedReferenceDeletionFromDoc.bind(this));
  }

  private handleNumberedReferenceDeletionFromDoc(numberedRef): void {
    if (numberedRef.referencedBy.length > 0) {
      this.selectedNumberedReferenceToDeleteFromDoc = numberedRef.citationId;
      this.decoratorActionsService
        .deleteNumberedReference(numberedRef, numberedRef.citationCount)
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            this.store.dispatch(
              setDeletedNumberedReferenceId({
                citationId: numberedRef.citationId,
              })
            );
          } else {
            this.selectedNumberedReferenceToDeleteFromDoc = null;
            this.store.dispatch(
              setDeletedFromDocNumberedReferenceId({ citationId: null })
            );
          }
        });
    }
  }
  // #endregion Private Methods (25)
}
